import { createSelector } from '@reduxjs/toolkit';

import type { AccentItem, LanguageItem, StyleItem, ToneItem } from './ai-voices-slices';

import type {
    AccentEnum,
    AIVoice,
    AIVoiceStyleEnum,
    AIVoiceToneEnum,
    LanguageEnum,
} from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { RootState } from 'js/src/libs/RootState';

export function getAIVoicesState(state: RootState) {
    return state.aiVoicesAndAvatars.aiVoices.voices;
}

function getAIVoicesStoreORM(aiVoicesState: ReturnType<typeof getAIVoicesState>) {
    const orm = new PlainJsStoreORM(aiVoicesState);
    return orm;
}

/**
 * @deprecated
 */
export function getAIVoiceIdsFromAIVoicesState(
    aiVoicesState: ReturnType<typeof getAIVoicesState>,
): string[] {
    const orm = getAIVoicesStoreORM(aiVoicesState);
    return orm.allIds;
}

/**
 * @deprecated
 */
export function getAIVoiceByIdFromAIVoicesState(
    aiVoicesState: ReturnType<typeof getAIVoicesState>,
    id: string,
): AIVoice | null {
    const orm = getAIVoicesStoreORM(aiVoicesState);
    const voice = orm.getById(id);
    return voice || null;
}

export function getAIVoiceIds(state: RootState): string[] {
    return getAIVoiceIdsFromAIVoicesState(getAIVoicesState(state));
}

export function getAIVoiceById(state: RootState, id: string): AIVoice | null {
    return getAIVoiceByIdFromAIVoicesState(getAIVoicesState(state), id);
}

export const getAIVoiceLanguages = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices.languages,
    (languagesData): LanguageItem[] => {
        return languagesData.allIds
            .map((id) => languagesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceAccents = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices.accents,
    (accentsData): AccentItem[] => {
        return accentsData.allIds
            .map((id) => accentsData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceTones = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices.tones,
    (tonesData): ToneItem[] => {
        return tonesData.allIds
            .map((id) => tonesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceStyles = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices.styles,
    (stylesData): StyleItem[] => {
        return stylesData.allIds
            .map((id) => stylesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export function getSelectedLanguages(state: RootState): LanguageEnum[] {
    return state.aiVoicesAndAvatars.aiVoices.filters.languages;
}

export function getSelectedStyle(state: RootState): AIVoiceStyleEnum | null {
    return state.aiVoicesAndAvatars.aiVoices.filters.style;
}

export function getSelectedAccent(state: RootState): AccentEnum | null {
    return state.aiVoicesAndAvatars.aiVoices.filters.accent;
}

export function getSelectedTone(state: RootState): AIVoiceToneEnum | null {
    return state.aiVoicesAndAvatars.aiVoices.filters.tone;
}

export function getSelectedSearchTerm(state: RootState): string {
    return state.aiVoicesAndAvatars.aiVoices.filters.searchTerm;
}
